import * as React from 'react';

import Cookies, { CookieSetOptions } from 'universal-cookie';

import { LoginResponse, VideoResponse, CookieData } from '../domain/types';
import {
  getSearchQueryFromLocation,
  replaceSearchQueryInCurrentUrl,
} from '../domain/urlSearch';
import { filterVideoResponseBySearch } from '../domain/videoSearch';

type VideoContextType = {
  loading: boolean;
  data: VideoResponse | null;
  filteredData: VideoResponse | null;
  searchQuery: string;
  search: (query: string) => void;
  clearSearch: () => void;
  email: string | null;
  login: (address: string) => void;
  register: (address: string, firstName: string, lastName: string, country: string, hasEn: boolean, hasDutch: boolean, language: string) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  accessCodes: {[key: string]: boolean};
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
};

const VideoContext = React.createContext<VideoContextType | null>(null);

const cookieOptions: CookieSetOptions = {
  path: '/',
  maxAge: 60 * 60 * 24 * 365,
  sameSite: 'none',
  secure: true,
};

export const VideoProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  
  const [loading, setLoading] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [email, setEmail] = React.useState<string | null>(null);
  const [data, setData] = React.useState<VideoResponse | null>(null);
  const [searchQuery, setSearchQuery] = React.useState(() =>
    getSearchQueryFromLocation(),
  );
  const [accessCodes, setAccessCodes] = React.useState<{[key: string]: boolean}>({});
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const search = React.useCallback((query: string) => {
    setSearchQuery(query);
    replaceSearchQueryInCurrentUrl(query);
  }, []);

  const clearSearch = React.useCallback(() => {
    setSearchQuery('');
    replaceSearchQueryInCurrentUrl('');
  }, []);

  React.useEffect(() => {
    const onPopState = () => {
      setSearchQuery(getSearchQueryFromLocation());
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const filteredData = React.useMemo(() => {
    if (!data) {
      return null;
    }
    return filterVideoResponseBySearch(data, searchQuery);
  }, [data, searchQuery]);

  const readCookies = (): CookieData => {
    const cookies = new Cookies();
    const address = cookies.get('alamiretv') ?? null;
    const preview = cookies.get('preview') ?? null;
    let isNewUser = cookies.get('isNewUser') ?? true;

    if (address !== null || preview !== null) {
      isNewUser = false;
    }

    return {
      address,
      preview,
      isNewUser,
    }
  };

  const setCookies = (address: string, preview: boolean) => {
    const cookies = new Cookies();
    cookies.set('alamiretv', address, cookieOptions);
    cookies.set('preview', preview, cookieOptions);
  };

  const fetchData = async (lang: 'en' | 'nl' = 'en') => {
    setLoading(true);

    const url = window.location.href;
    const isLocal = url.includes('localhost');
    const apiUrl = isLocal ? 'http://localhost:8000' : 'https://www.alamirefoundation.org';

    try {
      const response = await fetch(`${apiUrl}/${lang}/api/pages/317/`);
      const responseData = await response.json() as VideoResponse;

      setData(responseData);
      const codes = responseData.codes.reduce<{[key: string]: boolean}>((acc, code) => {
        acc[code.code] = code.expired;
        return acc;
      }, {});
      setAccessCodes(codes);

    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const login = React.useCallback(async (address: string) => {

    const url = 'https://www.alamirefoundation.org/nl/alamiretv/login/';

    try {
      const reponse = await fetch(url, {method: 'POST', body: JSON.stringify({address})});
      const responseData = await reponse.json() as LoginResponse;

      if (responseData.result.success) {
        console.log('logged in');
        setIsAuthenticated(true);
        setEmail(address);
        setCookies(address, responseData.result.preview ?? false);
        setShowAuthModal(false);
      }

    } catch (error) {
      console.log('error logging in');
      console.log(error); 
    }
  }, []);

  const register = React.useCallback(async (address: string, firstName: string, lastName: string, country: string, hasEn: boolean, hasDutch: boolean, language: string) => {
    const url = 'https://www.alamirefoundation.org/nl/alamiretv/subscribe/';
    // const url = 'http://localhost:8000/en/alamiretv/subscribe/';

    const body = {
      address,
      first_name: firstName,
      last_name: lastName,
      lang: language,
      country,
      english: hasEn,
      dutch: hasDutch,
    };

    try {
      const reponse = await fetch(url, {method: 'POST', body: JSON.stringify(body)});
      const responseData = await reponse.json() as LoginResponse;

      if (responseData.result.success) {
        console.log('registered');
        setIsAuthenticated(true);
        setEmail(address);
        setCookies(address, responseData.result.preview ?? false);
        setShowAuthModal(false);
      } else {
        console.log('not registered');
        console.log(responseData);
      }

    } catch (error) {
      console.log('error registering');
      console.log(error); 
    }
  }, []);

  React.useEffect(() => {
    fetchData();
    const result = readCookies();

    if (result.address) {
      login(result.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = React.useMemo(
    () => ({
      loading,
      data,
      filteredData,
      searchQuery,
      search,
      clearSearch,
      login,
      register,
      isAuthenticated,
      setIsAuthenticated,
      accessCodes,
      email,
      showAuthModal,
      setShowAuthModal,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loading,
      data,
      filteredData,
      searchQuery,
      search,
      clearSearch,
      login,
      register,
      isAuthenticated,
      setIsAuthenticated,
      accessCodes,
      email,
      showAuthModal,
    ],
  );

  return (
    <VideoContext.Provider value={value}>{children}</VideoContext.Provider>
  );
};

export const useVideos = () => {
  const context = React.useContext(VideoContext);
  if (!context) {
    throw new Error('useVideos must be used within a VideoProvider');
  }
  return context;
};
